import classNames from 'classnames'
import { observer } from 'mobx-react'
import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { CDNImage } from '~/components/business/CDNImage'
import FollowBtn from '~/components/business/FollowBtn'
import { MyAvatar } from '~/components/ui/MyAvatar'
import { MyIcon } from '~/components/ui/MyIcon'
import { MyImage } from '~/components/ui/MyImage'
import { MyLink } from '~/components/ui/MyLink'
import { TextReadMore } from '~/components/ui/TextReadMore'
import { getUserInfo_userinfo } from '~/graphqls/types'
import { useStores } from '~/hooks/useStores'
import { digitalUnit } from '~/utils'

const StyledUserHeader = styled.div`
  .bg {
    position: relative;
    &-img {
      height: 260px;
      border-radius: 16px;
      background-color: ${props => props.theme['neutral-100']};
    }
    &-count {
      backdrop-filter: blur(12px);
    }
  }

  .user {
    position: relative;
    padding: 16px 0 16px 168px;
    &-avatar {
      position: absolute;
      top: -39px;
      left: 24px;
      height: 120px;
      width: 120px;
    }
    &-info {
      &__name {
        font-size: 24px;
        line-height: 32px;
        font-weight: 600;
        color: ${props => props.theme['neutral-800']};
      }
      &__desc {
        display: inline-block;
        font-size: 12px;
        line-height: 20px;
        color: ${props => props.theme['neutral-400']};
      }
    }
  }
`

type userCountType = {
  /** 标签 */
  label: string
  /** 值 */
  val: number | string
  /** 跳转链接 */
  url?: string
}

type userRankType = {
  label: string
  val: number | string
  icon: string
  /** 跳转链接 */
  url: string
}

export const UserHeader: React.FC<{
  userinfo: getUserInfo_userinfo
}> = observer(({ userinfo }) => {
  const { store } = useStores()
  const [fansCount, setFansCount] = useState(
    Number(userinfo?.social?.fansCount || 0)
  )
  // userinfo变了, 更新fansCount，用户切换了用户主页
  useEffect(() => {
    setFansCount(Number(userinfo?.social?.fansCount || 0))
  }, [userinfo])

  /** 用户数据统计 */
  const userCount = useMemo<userCountType[]>(() => {
    return [
      {
        label: '关注',
        val: digitalUnit(userinfo?.social?.followCount),
        url: `/user/${userinfo?.social?.uid}/following`,
      },
      {
        label: '粉丝',
        val: digitalUnit(userinfo?.social?.fansCount),
        url: `/user/${userinfo?.social?.uid}/follower`,
      },
      {
        label: '获赞',
        val: digitalUnit(userinfo?.social?.likesCount),
      },
      {
        label: '发布内容',
        val: digitalUnit(userinfo?.social?.squareCount || 0),
      },
      {
        label: '被同款数',
        val: digitalUnit(userinfo?.social?.aiSameStyleCount || 0),
      },
    ]
  }, [userinfo])

  /** ai排行榜数据 */
  const userRank = useMemo<userRankType[]>(() => {
    return [
      {
        label: '同款榜',
        val: userinfo?.social?.aiSameStyleRank || 0,
        icon: 'hand-pointer',
        url: '/rank',
      },
      {
        label: '作画榜',
        val: userinfo?.social?.aiPictureRank || 0,
        icon: 'ai',
        url: '/rank/picture-count',
      },
      {
        label: '粉丝榜',
        val: userinfo?.social?.fansRank || 0,
        icon: 'users',
        url: '/rank/fans-count',
      },
    ]
  }, [userinfo])

  return (
    <StyledUserHeader>
      <div className="bg">
        <MyImage tag="v1200" className="bg-img" src={userinfo?.backgroundUrl} />
        {userinfo?.social?.uid === store.userinfo?.base?.uid && (
          <div className="absolute top-4 right-4 rounded-full bg-neutral-900 p-[10px] leading-none">
            <MyLink href="/settings/base" exist={true}>
              <MyIcon icon="edit" className="text-neutral-400 text-[20px]" />
            </MyLink>
          </div>
        )}
        <div className="bg-count absolute right-4 bottom-4 rounded-xl p-2 flex bg-neutral-50 bg-opacity-50 ">
          {userCount.map(item =>
            item.url ? (
              <Link href={item.url} key={item.label}>
                <div className="text-center px-4">
                  <div className="text-neutral-900 text-base">{item.val}</div>
                  <div className=" text-xs text-neutral-900 text-opacity-80">
                    {item.label}
                  </div>
                </div>
              </Link>
            ) : (
              <div className="text-center px-4" key={item.label}>
                <div className="text-neutral-900 text-base">{item.val}</div>
                <div className=" text-xs text-neutral-900 text-opacity-80">
                  {item.label}
                </div>
              </div>
            )
          )}
        </div>
      </div>

      <div className="user">
        <div className="user-avatar">
          <MyAvatar
            image={userinfo?.avatarUrl as string}
            isVip={userinfo?.memberDetail?.hold}
            className="border-[2px] border-solid border-neutral-100 rounded-full"
            iconClassName="!h-[48px] !w-[48px]"
            size={120}
          ></MyAvatar>
        </div>
        <div className="user-info">
          <div className="flex justify-between mb-3">
            <div className="flex items-center">
              <div className="user-info__name flex items-center">
                <span className="max-w-[520px] line-clamp-1">
                  {userinfo?.nickname}
                </span>
                {!!userinfo?.companyAuthInfo?.id && (
                  <CDNImage
                    src="/images/common/company.png"
                    className="h-6 ml-1"
                  />
                )}
                {/* medals */}
                {userinfo?.medals &&
                  userinfo?.medals?.slice(0, 3).map((medal, index) => {
                    return (
                      <div
                        className="ml-1 flex justify-center items-center"
                        key={index}
                      >
                        <img
                          className="w-6 h-6 z-10"
                          src={medal?.url!}
                          alt=""
                        />
                        <div
                          className="z-0 h-5 -ml-[15px] text-[12px] text-neutral-400 leading-[18px] text-center overflow-hidden
                        bg-neutral-200 border-[1px] rounded-[4px] border-solid border-neutral-300 pl-4 pr-[6px]"
                        >
                          {medal?.name?.substring(0, 10)!}
                        </div>
                      </div>
                    )
                  })}
              </div>
              <div className="ml-2">
                <FollowBtn
                  dark
                  hiddenIfSelf={true}
                  targetId={userinfo?.social?.uid}
                  status={userinfo?.social?.followStatus}
                  followCallback={isFollow => {
                    if (isFollow) {
                      setFansCount(fansCount + 1)
                    } else {
                      setFansCount(fansCount - 1)
                    }
                  }}
                />
              </div>
            </div>
            <div className="flex">
              {userRank.map(item => (
                <Link
                  className="flex items-center ml-4"
                  key={item.label}
                  href={item.url}
                >
                  <MyIcon icon={item.icon} className="text-neutral-400" />
                  <div className="text-neutral-400 text-base px-1">
                    {item.label}
                  </div>
                  <div
                    className={classNames('text-base', [
                      Number(item.val) <= 10 && Number(item.val) !== 0
                        ? 'bg-primary bg-opacity-20 h-5 leading-5 px-[6px] rounded-[4px] text-primary'
                        : `bg-neutral-200 h-5 leading-5 px-[2px] rounded-[4px] text-neutral-400 ${
                            Number(item.val) === 0 ? '!px-[6px]' : ''
                          }`,
                    ])}
                  >
                    {item.val || '未上榜'}
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <TextReadMore
            className="user-info__desc"
            text={
              userinfo?.personalizedSignature || '这个人很懒，什么都没有留下'
            }
            limit={105}
          />
        </div>
      </div>
    </StyledUserHeader>
  )
})
